import Phaser from 'phaser'
import { GAME_WIDTH, GAME_HEIGHT } from '../config'

/**
 * GameScene - Main gameplay scene
 * Demonstrates Cooking Mama-style mechanics:
 * - Drag and drop ingredients
 * - Timing-based interactions
 * - Score tracking
 */
export class GameScene extends Phaser.Scene {
  private score = 0
  private scoreText!: Phaser.GameObjects.Text
  private pot!: Phaser.GameObjects.Image
  private ingredients: Phaser.GameObjects.Image[] = []
  private addedCount = 0
  private totalIngredients = 5

  constructor() {
    super({ key: 'GameScene' })
  }

  create(): void {
    this.resetState()
    this.cameras.main.fadeIn(300)

    this.createBackground()
    this.createUI()
    this.createPot()
    this.createIngredients()
    this.createInstructions()
  }

  private resetState(): void {
    this.score = 0
    this.addedCount = 0
    this.ingredients = []
  }

  private createBackground(): void {
    // Kitchen-like background
    const graphics = this.add.graphics()

    // Counter/table surface
    graphics.fillStyle(0x8b4513)
    graphics.fillRect(0, GAME_HEIGHT - 150, GAME_WIDTH, 150)

    // Wood grain lines
    graphics.lineStyle(2, 0x654321, 0.3)
    for (let y = GAME_HEIGHT - 140; y < GAME_HEIGHT; y += 20) {
      graphics.lineBetween(0, y, GAME_WIDTH, y)
    }

    // Wall
    graphics.fillStyle(0xffecd2)
    graphics.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT - 150)

    // Tile pattern on wall
    graphics.lineStyle(1, 0xddd5c7, 0.5)
    for (let x = 0; x < GAME_WIDTH; x += 50) {
      graphics.lineBetween(x, 0, x, GAME_HEIGHT - 150)
    }
    for (let y = 0; y < GAME_HEIGHT - 150; y += 50) {
      graphics.lineBetween(0, y, GAME_WIDTH, y)
    }
  }

  private createUI(): void {
    // Score display
    this.scoreText = this.add.text(GAME_WIDTH - 20, 20, 'Score: 0', {
      fontFamily: 'system-ui',
      fontSize: '24px',
      fontStyle: 'bold',
      color: '#e65100',
      stroke: '#ffffff',
      strokeThickness: 3,
    })
    this.scoreText.setOrigin(1, 0)

    // Back button
    const backButton = this.add.text(20, 20, '← Menu', {
      fontFamily: 'system-ui',
      fontSize: '20px',
      color: '#e65100',
      backgroundColor: '#ffffff',
      padding: { x: 10, y: 5 },
    })
    backButton.setInteractive({ useHandCursor: true })
      .on('pointerover', () => backButton.setStyle({ color: '#ff8a50' }))
      .on('pointerout', () => backButton.setStyle({ color: '#e65100' }))
      .on('pointerup', () => {
        this.cameras.main.fadeOut(300)
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start('MainMenuScene')
        })
      })
  }

  private createPot(): void {
    // Create the pot/bowl where ingredients go
    this.pot = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT - 200, 'pot')
    this.pot.setScale(1.5)

    // Add drop zone
    const dropZone = this.add.zone(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 200,
      180,
      120
    )
    dropZone.setRectangleDropZone(180, 120)

    // Visual feedback for drop zone (debug)
    if (import.meta.env.DEV) {
      const graphics = this.add.graphics()
      graphics.lineStyle(2, 0x00ff00, 0.5)
      graphics.strokeRect(
        dropZone.x - dropZone.input!.hitArea.width / 2,
        dropZone.y - dropZone.input!.hitArea.height / 2,
        dropZone.input!.hitArea.width,
        dropZone.input!.hitArea.height
      )
    }
  }

  private createIngredients(): void {
    // Spawn ingredients at the top
    const startX = 100
    const spacing = 150

    for (let i = 0; i < this.totalIngredients; i++) {
      const x = startX + (i % 5) * spacing
      const y = 100 + Math.floor(i / 5) * 80

      const ingredient = this.add.image(x, y, `ingredient_${i % 5}`)
      ingredient.setInteractive({ useHandCursor: true, draggable: true })
      ingredient.setData('originalX', x)
      ingredient.setData('originalY', y)
      ingredient.setData('added', false)

      // Entrance animation
      ingredient.setScale(0)
      this.tweens.add({
        targets: ingredient,
        scale: 1,
        duration: 300,
        ease: 'Back.easeOut',
        delay: i * 100,
      })

      this.ingredients.push(ingredient)
    }

    // Set up drag events
    this.input.on('drag', (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image, dragX: number, dragY: number) => {
      gameObject.x = dragX
      gameObject.y = dragY
    })

    this.input.on('dragend', (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image, dropped: boolean) => {
      if (!dropped) {
        // Return to original position
        this.tweens.add({
          targets: gameObject,
          x: gameObject.getData('originalX'),
          y: gameObject.getData('originalY'),
          duration: 300,
          ease: 'Back.easeOut',
        })
      }
    })

    this.input.on('drop', (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image, _dropZone: Phaser.GameObjects.Zone) => {
      if (gameObject.getData('added')) return

      // Mark as added
      gameObject.setData('added', true)
      gameObject.disableInteractive()

      // Animate into pot
      this.tweens.add({
        targets: gameObject,
        x: this.pot.x + Phaser.Math.Between(-30, 30),
        y: this.pot.y - 20,
        scale: 0.5,
        alpha: 0.7,
        duration: 300,
        ease: 'Quad.easeIn',
        onComplete: () => {
          this.addIngredient()
        },
      })
    })
  }

  private addIngredient(): void {
    this.addedCount++
    this.score += 100

    // Update score with animation
    this.scoreText.setText(`Score: ${this.score}`)
    this.tweens.add({
      targets: this.scoreText,
      scale: 1.2,
      duration: 100,
      yoyo: true,
    })

    // Show feedback
    const feedbackText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '+100', {
      fontFamily: 'system-ui',
      fontSize: '48px',
      fontStyle: 'bold',
      color: '#4caf50',
      stroke: '#ffffff',
      strokeThickness: 4,
    })
    feedbackText.setOrigin(0.5)

    this.tweens.add({
      targets: feedbackText,
      y: feedbackText.y - 100,
      alpha: 0,
      duration: 800,
      ease: 'Quad.easeOut',
      onComplete: () => feedbackText.destroy(),
    })

    // Pot wobble animation
    this.tweens.add({
      targets: this.pot,
      angle: 5,
      duration: 50,
      yoyo: true,
      repeat: 3,
    })

    // Check if all ingredients added
    if (this.addedCount >= this.totalIngredients) {
      this.time.delayedCall(500, () => this.showComplete())
    }
  }

  private createInstructions(): void {
    const instructionText = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 50,
      '👆 Drag ingredients into the pot!',
      {
        fontFamily: 'system-ui',
        fontSize: '20px',
        color: '#ffffff',
        backgroundColor: '#00000066',
        padding: { x: 15, y: 8 },
      }
    )
    instructionText.setOrigin(0.5)

    // Fade out after a few seconds
    this.tweens.add({
      targets: instructionText,
      alpha: 0,
      duration: 500,
      delay: 3000,
      onComplete: () => instructionText.destroy(),
    })
  }

  private showComplete(): void {
    // Dim background
    const overlay = this.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      0x000000,
      0
    )
    this.tweens.add({
      targets: overlay,
      alpha: 0.7,
      duration: 300,
    })

    // Complete panel
    const panel = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2)

    const bg = this.add.rectangle(0, 0, 350, 250, 0xffffff, 0.95)
    bg.setStrokeStyle(4, 0xe65100)

    const titleText = this.add.text(0, -80, '🎉 Complete!', {
      fontFamily: 'system-ui',
      fontSize: '36px',
      fontStyle: 'bold',
      color: '#e65100',
    })
    titleText.setOrigin(0.5)

    const finalScore = this.add.text(0, -20, `Final Score: ${this.score}`, {
      fontFamily: 'system-ui',
      fontSize: '28px',
      color: '#333333',
    })
    finalScore.setOrigin(0.5)

    // Play again button
    const playAgainBg = this.add.rectangle(0, 50, 180, 50, 0xe65100)
    const playAgainText = this.add.text(0, 50, 'Play Again', {
      fontFamily: 'system-ui',
      fontSize: '22px',
      fontStyle: 'bold',
      color: '#ffffff',
    })
    playAgainText.setOrigin(0.5)

    playAgainBg.setInteractive({ useHandCursor: true })
      .on('pointerover', () => playAgainBg.setFillStyle(0xff8a50))
      .on('pointerout', () => playAgainBg.setFillStyle(0xe65100))
      .on('pointerup', () => this.scene.restart())

    panel.add([bg, titleText, finalScore, playAgainBg, playAgainText])

    // Entrance animation
    panel.setScale(0)
    this.tweens.add({
      targets: panel,
      scale: 1,
      duration: 400,
      ease: 'Back.easeOut',
    })
  }
}
